import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass, BookOpen, ArrowSquareOut, Lightbulb, ListChecks, Question,
  Sparkle, Receipt, Flask, Package, FileText, ChatCircle, X,
} from "@phosphor-icons/react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api } from "../lib/kostra";

const ICONS = {
  Receipt: <Receipt size={16} weight="duotone" />,
  Flask: <Flask size={16} weight="duotone" />,
  Sparkle: <Sparkle size={16} weight="duotone" />,
  Package: <Package size={16} weight="duotone" />,
  MagnifyingGlass: <MagnifyingGlass size={16} weight="duotone" />,
  FileText: <FileText size={16} weight="duotone" />,
  ChatCircle: <ChatCircle size={16} weight="duotone" />,
};

// Parse "(at `/route`)" hints in steps and prettify
const cleanStep = (s) => s.replace(/\(`?\/[^)`]+`?\)/g, (m) => m.replace(/[`(]/g, ""));

export default function GuidePage() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [allFunctions, setAllFunctions] = useState([]);
  const [moduleKey, setModuleKey] = useState("any");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [searchHits, setSearchHits] = useState([]);
  const [active, setActive] = useState(null); // selected function detail
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kostra:guide:recent") || "[]"); }
    catch { return []; }
  });

  // Load modules + all functions on mount (public)
  useEffect(() => {
    api.get("/guide/modules").then(({ data }) => setModules(data.items || []));
    api.get("/guide/functions").then(({ data }) => setAllFunctions(data.items || []));
  }, []);

  // Debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Search hits
  useEffect(() => {
    if (!debounced) { setSearchHits([]); return; }
    api.get(`/guide/search?q=${encodeURIComponent(debounced)}`)
       .then(({ data }) => setSearchHits(data.items || []))
       .catch(() => setSearchHits([]));
  }, [debounced]);

  // Filter list: by module + (only when not searching) show all of that module
  const visibleList = useMemo(() => {
    if (debounced) return searchHits;
    if (moduleKey && moduleKey !== "any") {
      return allFunctions.filter((f) => f.module_key === moduleKey);
    }
    return allFunctions;
  }, [debounced, searchHits, allFunctions, moduleKey]);

  const openFunction = (slug) => {
    api.get(`/guide/function/${slug}`).then(({ data }) => {
      setActive(data);
      // recent
      const next = [{ slug: data.slug, name: data.name, module: data.module_label },
                    ...recent.filter((x) => x.slug !== data.slug)].slice(0, 6);
      setRecent(next);
      try { localStorage.setItem("kostra:guide:recent", JSON.stringify(next)); } catch {}
    });
  };

  return (
    <div className="app-shell">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-9 pb-12">
          <div className="text-center">
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2 justify-center">
              <BookOpen size={14} weight="fill" /> Function Guide · Smart Search
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
              Every feature explained. <br className="hidden md:block" />
              <span className="text-[#D4AF37]">Search, learn, jump in.</span>
            </h1>
            <p className="text-white/70 text-sm md:text-base mt-2 max-w-2xl mx-auto">
              Search any function, tab or formula across the platform. Each entry shows definition,
              purpose, step-by-step, an example and a Pro Tip.
            </p>
          </div>

          {/* Search bar + module dropdown */}
          <div className="mt-7 max-w-4xl mx-auto bg-white rounded-full shadow-2xl flex items-center pl-5 pr-2 py-2 gap-3 border-2 border-[#D4AF37]/40 focus-within:border-[#D4AF37]"
               data-testid="guide-searchbar">
            <MagnifyingGlass size={20} weight="bold" className="text-[#0A3B24] shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search any function, tab, or feature..."
              className="flex-1 bg-transparent outline-none text-[#111827] text-base placeholder:text-[#9CA3AF] font-medium"
              data-testid="guide-search-input"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")}
                      className="h-9 w-9 rounded-full text-[#4B5563] hover:bg-[#FDF9EE] inline-flex items-center justify-center">
                <X size={14} weight="bold" />
              </button>
            )}
            <div className="hidden md:block w-px h-7 bg-[#E5E7EB]" />
            <Select value={moduleKey} onValueChange={setModuleKey}>
              <SelectTrigger className="border-0 shadow-none h-10 w-44 font-semibold text-[#0A3B24]"
                             data-testid="guide-module-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">All modules</SelectItem>
                {modules.map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left rail — module list & recent */}
          <aside className="lg:col-span-4 space-y-4">
            <div className="kostra-card p-4 md:p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-[#0A3B24] font-bold mb-3 inline-flex items-center gap-1.5">
                <ListChecks size={12} weight="bold" /> Modules
              </div>
              <div className="space-y-1.5" data-testid="guide-modules-list">
                <button type="button" onClick={() => setModuleKey("any")}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition ${
                          moduleKey === "any" ? "bg-[#0A3B24] text-white" : "text-[#111827] hover:bg-[#FDF9EE]"
                        }`}>
                  <span>All modules</span>
                  <span className="text-xs opacity-70 font-mono-num">{allFunctions.length}</span>
                </button>
                {modules.map((m) => {
                  const active = moduleKey === m.key;
                  return (
                    <button key={m.key} type="button" onClick={() => setModuleKey(m.key)}
                            data-testid={`guide-module-${m.key}`}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition ${
                              active ? "bg-[#0A3B24] text-white" : "text-[#111827] hover:bg-[#FDF9EE]"
                            }`}>
                      <span className="inline-flex items-center gap-2">{ICONS[m.icon] || <BookOpen size={14} />} {m.label}</span>
                      <span className={`text-xs font-mono-num ${active ? "opacity-80" : "opacity-50"}`}>{m.function_count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {recent.length > 0 && (
              <div className="kostra-card p-4 md:p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#0A3B24] font-bold mb-3 inline-flex items-center gap-1.5">
                  <Question size={12} weight="bold" /> Recently viewed
                </div>
                <div className="space-y-1" data-testid="guide-recent">
                  {recent.map((r) => (
                    <button key={r.slug} type="button" onClick={() => openFunction(r.slug)}
                            className="w-full text-left px-3 py-1.5 rounded-md text-sm text-[#111827] hover:bg-[#FDF9EE] transition">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#A87C0E]">{r.module}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>

          {/* Right column — list + detail */}
          <section className="lg:col-span-8">
            {!active ? (
              <>
                <div className="kostra-card p-4 mb-3 inline-flex items-center justify-between w-full">
                  <h2 className="font-display text-lg text-[#0A3B24]">
                    {debounced
                      ? `${visibleList.length} matches for "${debounced}"`
                      : moduleKey !== "any"
                        ? `${visibleList.length} functions in ${modules.find(m => m.key === moduleKey)?.label || ""}`
                        : `Browse all ${visibleList.length} functions`}
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="guide-results">
                  {visibleList.map((f) => (
                    <motion.button
                      key={f.slug} type="button"
                      onClick={() => openFunction(f.slug)}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18 }}
                      className="kostra-card p-4 text-left hover:border-[#D4AF37]/60 hover:shadow-md transition group"
                      data-testid={`guide-card-${f.slug}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider font-bold text-[#A87C0E]">
                        {f.module_label}
                      </div>
                      <div className="font-display text-base text-[#0A3B24] mt-0.5 leading-snug">
                        {f.name}
                      </div>
                      <p className="text-xs text-[#4B5563] mt-2 line-clamp-3">{f.definition}</p>
                      <div className="mt-3 text-[11px] text-[#0A3B24] font-semibold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                        Read more <ArrowSquareOut size={11} weight="bold" />
                      </div>
                    </motion.button>
                  ))}
                </div>
              </>
            ) : (
              <FunctionDetail
                fn={active}
                onClose={() => setActive(null)}
                onOpenFeature={() => navigate(active.route)}
              />
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function FunctionDetail({ fn, onClose, onOpenFeature }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="kostra-card overflow-hidden"
      data-testid={`guide-detail-${fn.slug}`}
    >
      {/* Header */}
      <div className="px-6 py-5 bg-gradient-to-br from-[#0A3B24] to-[#0E5232] text-white">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#D4AF37]">{fn.module_label}</div>
            <h2 className="font-display text-2xl md:text-3xl mt-1 leading-tight">{fn.name}</h2>
          </div>
          <button type="button" onClick={onClose}
                  className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center"
                  aria-label="Back to list">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button onClick={onOpenFeature}
                  className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                  data-testid="guide-open-feature">
            <ArrowSquareOut size={14} weight="bold" className="mr-1.5" /> Open this feature
          </Button>
          <span className="text-white/60 text-xs">
            {fn.route}{fn.tab ? ` · tab: ${fn.tab}` : ""}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-6 md:p-8 space-y-6">
        <Section icon={<BookOpen size={14} weight="bold" />} title="Definition">
          <p className="text-sm text-[#111827] leading-relaxed">{fn.definition}</p>
        </Section>

        <Section icon={<Sparkle size={14} weight="bold" />} title="Purpose">
          <p className="text-sm text-[#111827] leading-relaxed">{fn.purpose}</p>
        </Section>

        <Section icon={<ListChecks size={14} weight="bold" />} title="How to use">
          <ol className="space-y-2.5">
            {(fn.how_to || []).map((s, i) => (
              <li key={i} className="flex gap-3 items-start">
                <span className="shrink-0 h-6 w-6 rounded-full bg-[#0A3B24] text-white text-xs font-bold inline-flex items-center justify-center font-mono-num">
                  {i + 1}
                </span>
                <span className="text-sm text-[#111827] leading-relaxed">{cleanStep(s)}</span>
              </li>
            ))}
          </ol>
        </Section>

        <Section icon={<Question size={14} weight="bold" />} title="Example">
          <div className="text-sm text-[#111827] leading-relaxed bg-[#F9FAFB] border-l-4 border-[#0A3B24] p-3 rounded-r-md">
            {fn.example}
          </div>
        </Section>

        <div className="rounded-xl border-l-4 border-[#D4AF37] bg-[#FDF9EE] p-4 flex gap-3 items-start">
          <Lightbulb size={20} weight="fill" className="text-[#D4AF37] shrink-0 mt-0.5" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#A87C0E]">Pro Tip</div>
            <div className="text-sm text-[#0A3B24] mt-1 leading-relaxed">{fn.pro_tip}</div>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function Section({ icon, title, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-[#0A3B24] font-bold mb-2 inline-flex items-center gap-1.5">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}
